import { defineStore } from "pinia";
import { ref } from "vue";

import { apiClient } from "@/client";
import type {
	Inspection,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
} from "@/type/api";

export const useStoreInspection = defineStore("inspection", () => {
	// State
	const _byID = ref<Map<number, Inspection>>(new Map());
	const loading = ref(false);
	const error = ref<string | null>(null);

	// Actions
	/**
	 * One page of inspections through the generic QUERY endpoint
	 * (doc/in-progress/query.md §11 Phase 4). The items are the review list's
	 * own shape — the resource projects the same row both surfaces answer with —
	 * and the envelope's `filtered_total` is the district's live inspection
	 * count, tombstones excluded by the collection's standing predicate.
	 */
	async function list(
		limit: number,
		offset: number,
	): Promise<MapReviewQueryEnvelope<Inspection>> {
		loading.value = true;
		error.value = null;
		try {
			const page = await apiClient.JSONQuery<
				MapReviewQueryEnvelope<Inspection>
			>("/api/inspection", { limit, offset } satisfies MapReviewQueryBody);
			// The list rows carry the whole detail shape, but they are still
			// not seeded into the by-id cache: the cache is detail-only, so a
			// detail view always reflects a fresh fetch.
			return page;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load inspections";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	async function fetchByID(id: number): Promise<Inspection> {
		const cached = _byID.value.get(id);
		if (cached) {
			return cached;
		}
		loading.value = true;
		error.value = null;
		try {
			const inspection = await apiClient.JSONGet<Inspection>(
				`/api/inspection/${id}`,
			);
			_byID.value.set(inspection.id, inspection);
			return inspection;
		} catch (err) {
			error.value =
				err instanceof Error ? err.message : "Failed to load inspection";
			throw err;
		} finally {
			loading.value = false;
		}
	}

	return {
		// Actions
		list,
		fetchByID,
		loading,
		error,
	};
});
